import algoliasearch from 'algoliasearch/lite'
import cookies from 'js-cookie'
import { shape, string } from 'prop-types'
import { Component } from 'react'
import { Configure, InstantSearch } from 'react-instantsearch-dom'

import {
  DISJUNCTIVE_FACETS,
  HITS_PER_PAGE_PRODUCT_FILTER,
} from 'shared/consts/algolia'
import { IS_NODE } from 'shared/consts/env'
import { PAGE_TYPE_SEARCH_RESULTS } from 'shared/consts/page'
import { experimentCompeteraIsEnabledAndV1 } from 'shared/experiments/components/experimentCompetera'
import { TYPE_SERP_LOAD_MORE } from 'shared/experiments/components/SerpLoadMore/consts'
import {
  getHitsPerPage,
  shouldModifyHitsPerPage,
} from 'shared/experiments/components/SerpLoadMore/helpers'
import { isMarketplace } from 'shared/experiments/utils/featureFlags'
import store, { subscribe } from 'shared/store'
import { SEARCH_RESULTS_UPDATED } from 'shared/store/ducks/events'
import { parseSearchStateToUrl } from 'shared/utils/algolia'
import { enhanceSearchClientWithAds } from 'shared/utils/algolia/enhanceSearchClientWithAds'
import {
  getCategoryPageDefaultSortingIndex,
  getTenantSpecificFilters,
} from 'shared/utils/algolia/filters'
import { getNoveltiesIndexName } from 'shared/utils/algolia/getNoveltiesIndexName'
import { hasResults } from 'shared/utils/algolia/resultsState'
import {
  getSearchStateWithPzns,
  getSearchUrl,
} from 'shared/utils/algolia/searchState'
import algoliaUserService from 'views/assets/scripts/services/storage/AlgoliaUserService'
import NoResults from 'views/components/organisms/SearchNoResults'
import SearchResults from 'views/components/organisms/SearchResults'
import { SearchStateAndUrlContext } from 'views/enhancers/withSearchStateAndUrlContext'
import debounce from 'views/utils/debounce'
import { cmsPagePropertiesType, publicConfigType } from 'views/utils/propTypes'

/**
 * The component is being rendered at different phases:
 *  1. A render made by calling `getSearchResultsState` in the controllers (not visible)
 *  2. Server side rendering
 *  3. Hydration on the client
 */
class AlgoliaInstantSearchContainer extends Component {
  static DEBOUNCE_TIME = 400

  constructor(props) {
    super(props)

    const {
      publicConfig,
      pageProperties,
      pageProperties: {
        declineAllRequests,
        searchConfig: {
          algolia: { appId, apiKey },
        },
      },
    } = this.props

    this.state = this.getInitialState()

    const algoliaSearchClient = algoliasearch(appId, apiKey)
    algoliaSearchClient.addAlgoliaAgent('fock - AlgoliaInstantSearchContainer')

    this.searchClient = enhanceSearchClientWithAds({
      searchClient: algoliaSearchClient,
      pageProperties,
      publicConfig,
      cookies,
    })

    if (declineAllRequests) {
      this.searchClient = {
        ...this.searchClient,
        search: () => {
          return
        },
      }
    }
  }

  debouncedPushState = debounce(({ searchState, urlOptions }) => {
    window.history.pushState(
      searchState,
      null,
      parseSearchStateToUrl(searchState, urlOptions) || window.location.pathname
    )
  }, AlgoliaInstantSearchContainer.DEBOUNCE_TIME)

  handleLoadMoreInitial = () => {
    const { hitsPerPage } = this.state

    this.setState({
      hitsPerPage,
    })
  }

  handleLoadMore = ({ refineNext }) => refineNext()

  searchConfigToUse = (searchConfig, experiments) =>
    experimentCompeteraIsEnabledAndV1(experiments) &&
    searchConfig.algoliaCompetera
      ? searchConfig.algoliaCompetera
      : searchConfig.algolia

  getInitialState() {
    const {
      pageProperties: {
        experiments,
        searchState,
        resultsState = {},
        searchUrl,
        searchConfig,
        expa,
        ruleContexts,
      },
      tenant,
    } = this.props

    const { indexName, mpIndexName } = this.searchConfigToUse(
      searchConfig,
      experiments
    )

    const hitsPerPage =
      getHitsPerPage(experiments) || HITS_PER_PAGE_PRODUCT_FILTER

    const ruleContextsList = []

    if (expa) {
      ruleContextsList.push(expa)
    }
    if (ruleContexts?.length) {
      ruleContextsList.push(...ruleContexts)
    }
    const defaultIndex = isMarketplace(experiments, tenant)
      ? mpIndexName
      : indexName

    const searchStateWithPzns = getSearchStateWithPzns(searchState)

    return {
      searchUrl,
      searchState: searchStateWithPzns,
      hitsPerPage,
      ruleContextsList,
      hasLoadMoreLogic: shouldModifyHitsPerPage(experiments),
      isEmpty: !hasResults(resultsState),
      indexName: defaultIndex,
    }
  }

  componentDidMount() {
    window.addEventListener('popstate', this.onPopState)

    if (this.state.hasLoadMoreLogic) {
      subscribe.after.once(TYPE_SERP_LOAD_MORE, this.handleLoadMoreInitial)
      subscribe.after(TYPE_SERP_LOAD_MORE, this.handleLoadMore)
    }
  }

  componentWillUnmount() {
    window.removeEventListener('popstate', this.onPopState)
  }

  onPopState = () => {
    // Fix for the Back button clicked in the browser
    window.location.reload()
  }

  onSearchStateChange = nextSearchState => {
    const {
      pageProperties: { pageType },
      userToken = algoliaUserService.getToken(),
    } = this.props
    const {
      searchUrl,
      searchState: { analytics, query = '', enableABTest },
      indexName: defaultIndex,
    } = this.state

    const indexName = query
      ? defaultIndex
      : getCategoryPageDefaultSortingIndex(defaultIndex)

    const urlOptions = {
      indexName,
      userToken: pageType === PAGE_TYPE_SEARCH_RESULTS ? userToken : undefined,
    }

    this.debouncedPushState({
      searchState: nextSearchState,
      urlOptions,
      analytics,
      enableABTest,
    })

    this.setState({
      enableABTest,
      analytics,
      searchState: nextSearchState,
      searchUrl: getSearchUrl({
        searchUrl,
        searchState: nextSearchState,
        indexName,
        userToken:
          pageType === PAGE_TYPE_SEARCH_RESULTS ? userToken : undefined,
      }),
      hitsPerPage: nextSearchState.hitsPerPage,
    })

    store.dispatch({
      type: SEARCH_RESULTS_UPDATED,
      searchState: nextSearchState,
    })
  }

  render() {
    const {
      userToken: algoliaUserToken,
      pageProperties: {
        experiments,
        title,
        pageType,
        resultsState,
        shouldShowVariantButtonsOnHits,
        logoPath,
        topNav,
        upNavTree,
        secondaryNavTitle,
        secondaryNavTree,
        infoText,
        searchConfig,
      },
      tenant,
    } = this.props

    const tenantSpecificFilters = getTenantSpecificFilters(tenant)

    const {
      isEmpty,
      searchState,
      searchState: {
        query: searchQuery = '',
        filters = tenantSpecificFilters,
        analyticsTags,
        enableABTest,
        analytics,
        disableNeuralSearch,
      },
      searchUrl,
      indexName,
      ruleContextsList,
    } = this.state

    const userToken =
      pageType === PAGE_TYPE_SEARCH_RESULTS && !(algoliaUserToken || IS_NODE)
        ? algoliaUserService.getToken()
        : algoliaUserToken
    // NB There is a content flickering before hydration wo `this.props` spreading:
    // a first render is made by `findResultsState` so not all props were provided
    return (
      <div className="u-margin-x-large--bottom">
        <InstantSearch
          {...this.props}
          indexName={indexName}
          searchState={searchState}
          resultsState={resultsState}
          onSearchStateChange={this.onSearchStateChange}
          searchClient={this.searchClient}
        >
          <Configure
            hitsPerPage={HITS_PER_PAGE_PRODUCT_FILTER}
            filters={filters}
            disjunctiveFacets={DISJUNCTIVE_FACETS}
            query={searchQuery}
            facetingAfterDistinct
            getRankingInfo
            analytics={analytics}
            clickAnalytics
            analyticsTags={analyticsTags}
            disableNeuralSearch={disableNeuralSearch}
            userToken={userToken}
            ruleContexts={ruleContextsList}
            enableABTest={enableABTest}
            distinct={shouldShowVariantButtonsOnHits}
          />
          <SearchStateAndUrlContext.Provider
            value={{
              searchState,
              searchUrl,
              shouldShowVariantButtonsOnHits,
            }}
          >
            <SearchResults
              hidden={isEmpty}
              query={searchQuery}
              pageType={pageType}
              title={title}
              logoPath={logoPath}
              topNav={topNav}
              upNavTree={upNavTree}
              secondaryNavTitle={secondaryNavTitle}
              secondaryNavTree={secondaryNavTree}
              infoText={infoText}
              indexName={indexName}
              noveltiesIndexName={getNoveltiesIndexName(
                isMarketplace(experiments, tenant),
                this.searchConfigToUse(searchConfig, experiments)
              )}
            />
          </SearchStateAndUrlContext.Provider>
          {isEmpty && <NoResults query={searchQuery} />}
        </InstantSearch>
      </div>
    )
  }
}

AlgoliaInstantSearchContainer.propTypes = {
  pageProperties: shape(cmsPagePropertiesType).isRequired,
  publicConfig: shape(publicConfigType).isRequired,
  userToken: string,
  tenant: string,
}

AlgoliaInstantSearchContainer.defaultProps = {
  userToken: undefined,
  tenant: null,
}

export default AlgoliaInstantSearchContainer
